/**
 * m3 echo Frontend - Jobs React Query Hooks
 *
 * Custom hooks for managing job data with automatic caching,
 * background refetching, and WebSocket integration.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { jobsApi } from '../services/api';
import type { JobDetail, JobHistory } from '../types/api';
import { useEffect } from 'react';
import { useWebSocketStore } from '../stores/useWebSocketStore';
import { getIdentityScope } from '../stores/useAuthStore';

/**
 * Query key factory for jobs
 *
 * Scoped to the active identity so cached job data cannot survive an account
 * or tenant switch. `all` stays unscoped for broad invalidations.
 */
export const jobKeys = {
  all: ['jobs'] as const,
  scoped: () => [...jobKeys.all, getIdentityScope()] as const,
  lists: () => [...jobKeys.scoped(), 'list'] as const,
  list: (filters?: Record<string, any>) => [...jobKeys.lists(), { filters }] as const,
  details: () => [...jobKeys.scoped(), 'detail'] as const,
  detail: (id: number) => [...jobKeys.details(), id] as const,
  history: (id: number) => [...jobKeys.detail(id), 'history'] as const,
};

/**
 * Fetch a single job by ID
 */
export const useJob = (id: number | undefined) => {
  return useQuery({
    queryKey: jobKeys.detail(id!),
    queryFn: async () => {
      const response = await jobsApi.getById(id!);
      return response.data as JobDetail;
    },
    enabled: !!id,
    staleTime: 5000, // 5 seconds (short because jobs can change quickly)
    gcTime: 300000,  // 5 minutes
  });
};

/**
 * Fetch job history
 */
export const useJobHistory = (id: number | undefined) => {
  return useQuery({
    queryKey: jobKeys.history(id!),
    queryFn: async () => {
      const response = await jobsApi.getHistory(id!);
      return response.data as JobHistory;
    },
    enabled: !!id,
    staleTime: 10000, // 10 seconds
    gcTime: 300000,   // 5 minutes
  });
};


/**
 * Fetch job with WebSocket progress tracking
 * Automatically subscribes to WebSocket updates for running jobs
 * and invalidates queries when job completes
 */
export const useJobWithProgress = (id: number | undefined) => {
  const queryClient = useQueryClient();
  const { jobProgress, subscribeToJob, unsubscribeFromJob } = useWebSocketStore();

  // Fetch job data
  const jobQuery = useJob(id);

  // Subscribe to WebSocket updates for running jobs
  useEffect(() => {
    if (!id || !jobQuery.data) return;

    const job = jobQuery.data;
    const isRunning = job.status === 'running' || job.status === 'pending';

    if (isRunning) {
      console.log(`[useJobWithProgress] Subscribing to job ${id}`);

      subscribeToJob(id, () => {
        // When job completes, invalidate the query to refetch
        console.log(`[useJobWithProgress] Job ${id} completed, invalidating query`);
        queryClient.invalidateQueries({ queryKey: jobKeys.detail(id) });
      });
    }

    return () => {
      if (isRunning) {
        console.log(`[useJobWithProgress] Cleaning up subscription for job ${id}`);
        unsubscribeFromJob(id);
      }
    };
  }, [id, jobQuery.data?.status, subscribeToJob, unsubscribeFromJob, queryClient]);

  return {
    ...jobQuery,
    progress: id ? jobProgress[id] : undefined,
  };
};

/**
 * Hook to track progress for multiple jobs
 * Useful for project detail pages where multiple jobs are displayed
 */
export const useJobsProgress = (jobIds: number[]) => {
  const queryClient = useQueryClient();
  const { jobProgress, subscribeToJob, unsubscribeFromJob } = useWebSocketStore();

  useEffect(() => {
    if (!jobIds || jobIds.length === 0) return;

    console.log(`[useJobsProgress] Subscribing to ${jobIds.length} jobs`);

    // Track which jobs we've seen status changes for to avoid duplicate invalidations
    const statusChangeHandlers = new Map<number, NodeJS.Timeout>();

    jobIds.forEach(jobId => {
      subscribeToJob(jobId, () => {
        // When we receive ANY progress update, invalidate queries to update status
        // Use debouncing to avoid too many invalidations
        if (statusChangeHandlers.has(jobId)) {
          clearTimeout(statusChangeHandlers.get(jobId)!);
        }

        const timeoutId = setTimeout(() => {
          console.log(`[useJobsProgress] Job ${jobId} status changed, invalidating queries`);

          // Invalidate the job detail query
          queryClient.invalidateQueries({ queryKey: jobKeys.detail(jobId) });

          // Also invalidate project queries to update the jobs list
          queryClient.invalidateQueries({ queryKey: ['projects'] });

          statusChangeHandlers.delete(jobId);
        }, 500); // 500ms debounce

        statusChangeHandlers.set(jobId, timeoutId);
      });
    });

    return () => {
      console.log(`[useJobsProgress] Cleaning up subscriptions for ${jobIds.length} jobs`);

      // Clear any pending timeouts
      statusChangeHandlers.forEach(timeoutId => clearTimeout(timeoutId));
      statusChangeHandlers.clear();

      jobIds.forEach(jobId => unsubscribeFromJob(jobId));
    };
  }, [jobIds.join(','), subscribeToJob, unsubscribeFromJob, queryClient]);

  return jobProgress;
};
